import React, { useCallback } from 'react';
import { DialogContent, Link } from '@material-ui/core';
import PropTypes from 'prop-types';

import { CustomDialog } from 'src/components/CustomDialog';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { CHANNEL } from 'src/data/channel';
import { UPGRADE_DIALOG_ACTIONS_ORIGIN } from 'src/data/common';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import {
  ADS_CHANNELS,
  BETA_CHANNELS,
  EMAILS_CHANNELS,
  isBetaChannel,
  SOCIAL_CHANNELS,
} from 'src/data/performance';
import { useCheckChannelConnected } from 'src/services';
import { useUpgradePopup } from 'src/services/popup';
import {
  useCustomerSalesforceCrmIntegrationEnabled,
  useCustomerTwitterSocialIntegrationEnabled,
  useCustomFeatureLimitsData,
} from 'src/store';

import { Channel } from './components/Channel';
import { useStyles } from './useStyles';

export const ConnectIntegrationPopup = ({ open, onClose, onIntegrationClick }) => {
  const styles = useStyles();

  const shouldIncludeTwitterSocial = useCustomerTwitterSocialIntegrationEnabled();
  const shouldIncludeSalesforceCrm = useCustomerSalesforceCrmIntegrationEnabled();

  const allChannels = [
    ...ADS_CHANNELS,
    ...SOCIAL_CHANNELS,
    ...(shouldIncludeTwitterSocial ? [CHANNEL.twitterSocial] : []), // Conditionally include CHANNEL.twitterSocial
    ...(shouldIncludeSalesforceCrm ? [CHANNEL.salesforceCrm] : []), // Conditionally include CHANNEL.salesforceCrm
    ...EMAILS_CHANNELS,
    CHANNEL.emailChannel,
    CHANNEL.projectWtm,
    ...BETA_CHANNELS,
  ];

  const checkIsChannelConnected = useCheckChannelConnected();
  const openUpgradePopup = useUpgradePopup({
    actionsOrigin: UPGRADE_DIALOG_ACTIONS_ORIGIN.cipPageConnectDialog,
  });

  const { limit, currentCount, limitedAccountData } = useCustomFeatureLimitsData(
    FEATURE_LIMITS.resourcesRowsPerCustomer
  );
  const isFeatureLimited = limit && (!!limitedAccountData?.accountId || limit <= currentCount);

  const handleChannelClick = useCallback(
    (channel) => {
      if (isFeatureLimited && !isBetaChannel(channel)) {
        openUpgradePopup(FEATURE_LIMITS.resourcesRowsPerCustomer);
        return;
      }
      onIntegrationClick({ channel });
      if (!isBetaChannel(channel)) {
        onClose();
      }
    },
    [onIntegrationClick, onClose, isFeatureLimited, openUpgradePopup]
  );

  return (
    <CustomDialog
      open={open}
      className={styles.root}
      classes={{ dialogTitle: styles.dialogTitle }}
      onClose={onClose}
      maxWidth="lg"
      data-testid="ConnectIntegrationPopup"
      hideActions
      title="New integration"
      displayCloseIcon
    >
      <DialogContent className={styles.dialogContent}>
        <CustomTypography
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.regular16}
        >
          Connect a new integration to Anyword or add a new account to an existing integration
        </CustomTypography>
        <div className={styles.wrapper}>
          {allChannels.map((channel) => (
            <Channel
              key={channel}
              channel={channel}
              isConnected={checkIsChannelConnected(channel)}
              onClick={handleChannelClick}
            />
          ))}
        </div>
        <CustomTypography
          className={styles.missingIntegration}
          variant={TYPOGRAPHY_VARIANT.regular14}
        >
          Can't find the integration you need?{' '}
          <Link
            href="https://forms.monday.com/forms/6062c3042c055b9487a5d920f2625311?r=use1"
            target="_blank"
          >
            Request an integration
          </Link>
        </CustomTypography>
      </DialogContent>
    </CustomDialog>
  );
};

ConnectIntegrationPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onIntegrationClick: PropTypes.func.isRequired,
};
